import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Globe, Home as HomeIcon } from "lucide-react";
import { useT } from "@/lib/i18n";
import { UserMenu } from "@/components/UserMenu";

const LOGO_URL =
  "/logo.png";

export default function Navbar() {
  const { t, lang, setLang } = useT();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const onHome = location.pathname === "/academy";

  const navLinks = [
    { href: "#home", label: t("nav_home"), key: "home" },
    { href: "#contact", label: t("nav_contact"), key: "contact" },
    { href: "#instagram", label: t("nav_instagram"), key: "instagram" },
    { href: "#faq", label: t("nav_faq"), key: "faq" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const dark = scrolled || !onHome;
  const toggleLang = () => setLang(lang === "en" ? "el" : "en");

  return (
    <>
      <header
        data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          dark
            ? "backdrop-blur-xl bg-white/85 border-b border-slate-200/60 py-3"
            : "bg-transparent py-5"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between gap-4">
          <Link
            to="/"
            data-testid="nav-landing-btn"
            title={t("nav_landing")}
            className={`shrink-0 h-10 w-10 rounded-full flex items-center justify-center transition-colors ${
              dark ? "bg-slate-100 text-slate-700 hover:bg-slate-200" : "bg-white/15 text-white hover:bg-white/25"
            }`}
          >
            <HomeIcon className="h-5 w-5" />
          </Link>
          <Link to="/academy" data-testid="nav-logo" className="flex items-center gap-3 shrink-0 mr-auto">
            <img
              src={LOGO_URL}
              alt="The Sand Lab"
              className={`h-12 sm:h-14 w-auto transition-all ${
                dark ? "" : "brightness-0 invert"
              }`}
            />
          </Link>

          {onHome && (
            <nav className="hidden lg:flex items-center gap-7">
              {navLinks.map((l) => (
                <a
                  key={l.key}
                  href={l.href}
                  data-testid={`nav-link-${l.key}`}
                  className={`text-sm font-semibold transition-colors ${
                    dark
                      ? "text-slate-700 hover:text-ocean"
                      : "text-white/90 hover:text-white"
                  }`}
                >
                  {l.label}
                </a>
              ))}
              <Link
                to="/training"
                data-testid="nav-link-training"
                className={`text-sm font-semibold transition-colors ${
                  dark
                    ? "text-ocean hover:text-ocean-hover"
                    : "text-white/90 hover:text-white"
                }`}
              >
                {t("train_nav")}
              </Link>
            </nav>
          )}

          <nav className="hidden md:flex items-center gap-5 mr-4">
            <Link
              to="/tournaments"
              data-testid="nav-link-tournaments"
              className={`text-sm font-semibold transition-colors ${dark ? "text-slate-700 hover:text-ocean" : "text-white/90 hover:text-white"}`}
            >
              {t("nav_tournaments")}
            </Link>
            <Link
              to="/courts"
              data-testid="nav-link-courts"
              className={`text-sm font-semibold transition-colors ${dark ? "text-slate-700 hover:text-ocean" : "text-white/90 hover:text-white"}`}
            >
              {t("nav_courts")}
            </Link>
          </nav>

          <div className="hidden md:flex items-center gap-2 shrink-0">
            <button
              onClick={toggleLang}
              data-testid="lang-toggle"
              className={`flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest px-3 py-2 rounded-full transition-colors ${
                dark
                  ? "text-slate-700 hover:bg-slate-100"
                  : "text-white/90 hover:bg-white/10"
              }`}
              title="Toggle language"
            >
              <Globe className="h-4 w-4" />
              {lang === "en" ? "EN" : "EL"}
            </button>
            <Link
              to="/admin"
              data-testid="nav-admin-link"
              className={`text-sm font-semibold px-4 py-2 rounded-full transition-colors ${
                dark ? "text-ocean hover:bg-ocean/10" : "text-white hover:bg-white/15"
              }`}
            >
              {t("nav_admin")}
            </Link>
            <UserMenu dark={dark} />
            {onHome && (
              <a
                href="#contact"
                data-testid="nav-register-btn"
                className="px-5 py-2.5 rounded-full bg-sunset text-white font-semibold text-sm hover:bg-sunset-hover transition-colors shadow-sunset"
              >
                {t("nav_contact")}
              </a>
            )}
          </div>

          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className={`md:hidden p-2 rounded-full ${
              dark ? "text-slate-900" : "text-white"
            }`}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {open && (
          <div className="md:hidden mt-3 mx-4 rounded-3xl bg-white shadow-soft border border-slate-100 p-5 space-y-3">
            {onHome &&
              navLinks.map((l) => (
                <a
                  key={l.key}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-link-${l.key}`}
                  className="block text-base font-semibold text-slate-700 hover:text-ocean"
                >
                  {l.label}
                </a>
              ))}
            <div className="border-t border-slate-100 pt-3 flex items-center justify-between gap-3">
              <button
                onClick={() => {
                  toggleLang();
                  setOpen(false);
                }}
                data-testid="lang-toggle-mobile"
                className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest px-3 py-2 rounded-full text-slate-700 bg-slate-100"
              >
                <Globe className="h-4 w-4" />
                {lang === "en" ? "EN → EL" : "EL → EN"}
              </button>
            </div>
            <Link
              to="/tournaments"
              onClick={() => setOpen(false)}
              data-testid="nav-mobile-tournaments"
              className="block text-base font-semibold text-slate-700"
            >
              {t("nav_tournaments")}
            </Link>
            <Link
              to="/courts"
              onClick={() => setOpen(false)}
              data-testid="nav-mobile-courts"
              className="block text-base font-semibold text-slate-700"
            >
              {t("nav_courts")}
            </Link>
            <Link
              to="/training"
              onClick={() => setOpen(false)}
              data-testid="nav-mobile-training"
              className="block text-base font-semibold text-sunset"
            >
              {t("train_nav")}
            </Link>
            <Link
              to="/admin"
              onClick={() => setOpen(false)}
              data-testid="nav-mobile-admin"
              className="block text-base font-semibold text-ocean"
            >
              {t("nav_admin")}
            </Link>
            <div className="pt-2 border-t border-slate-100"><UserMenu dark /></div>
          </div>
        )}
      </header>

    </>
  );
}
